/**
 * Copyright (c) 2020 Google Inc
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
 * the Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
 * FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
 * IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
 * CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

const exposed = {}
if (location.search) {
	var a = document.createElement('a')
	a.href = location.href
	a.search = ''
	history.replaceState(null, null, a.href)
}

function tweet_(url) {
	open('https://twitter.com/intent/tweet?url=' + encodeURIComponent(url), '_blank')
}
function tweet(anchor) {
	tweet_(anchor.getAttribute('href'))
}
expose('tweet', tweet)

function share(anchor) {
	var url = anchor.getAttribute('href')
	event.preventDefault()
	if (navigator.share) {
		navigator.share({
			url: url,
		})
	} else if (navigator.clipboard) {
		navigator.clipboard.writeText(url)
		message('Article URL copied to clipboard.')
	} else {
		tweet_(url)
	}
}
expose('share', share)

function toggleMenu(e) {
	document.body.classList.toggle('nav-open')
}
expose('toggleMenu', toggleMenu)

function message(msg) {
	var dialog = document.getElementById('message')
	dialog.textContent = msg
	dialog.setAttribute('open', '')
	setTimeout(function () {
		dialog.removeAttribute('open')
	}, 3000)
}

// import "./plugins/prefetch.js"
function prefetch(e) {
	if (e.target.tagName != 'A') {
		return
	}
	if (e.target.origin != location.origin) {
		return
	}
	/**
	 * Return the given url with no fragment
	 * @param {string} url potentially containing a fragment
	 * @return {string} url without fragment
	 */
	const removeUrlFragment = (url) => url.split('#')[0]
	if (removeUrlFragment(window.location.href) === removeUrlFragment(e.target.href)) {
		return
	}
	var l = document.createElement('link')
	l.rel = 'prefetch'
	l.href = e.target.href
	document.head.appendChild(l)
}
document.documentElement.addEventListener('mouseover', prefetch, {
	capture: true,
	passive: true,
})
document.documentElement.addEventListener('touchstart', prefetch, {
	capture: true,
	passive: true,
})

const GA_ID = document.documentElement.getAttribute('ga-id')
window.ga =
	window.ga ||
	function () {
		if (!GA_ID) {
			return
		}
		;(ga.q = ga.q || []).push(arguments)
	}
ga.l = +new Date()
ga('create', GA_ID, 'auto')
ga('set', 'transport', 'beacon')
var timeout = setTimeout(
	(onload = function () {
		clearTimeout(timeout)
		ga('send', 'pageview')
	}),
	1000
)

var ref = +new Date()
function ping(event) {
	var now = +new Date()
	if (now - ref < 1000) {
		return
	}
	ga('send', {
		hitType: 'event',
		eventCategory: 'page',
		eventAction: event.type,
		eventLabel: Math.round((now - ref) / 1000),
	})
	ref = now
}
addEventListener('pagehide', ping)
addEventListener('visibilitychange', ping)

/**
 * Injects a script into document.head
 * @param {string} src path of script to be injected in <head>
 * @return {Promise} Promise object that resolves on script load event
 */
const dynamicScriptInject = (src) => {
	return new Promise((resolve) => {
		const script = document.createElement('script')
		script.src = src
		script.type = 'text/javascript'
		document.head.appendChild(script)
		script.addEventListener('load', () => {
			resolve(script)
		})
	})
}

/**
 * Injects a script into document.head
 * @param {string} src path of script to be injected in <head>
 * @return {Promise} Promise object that resolves on script load event
 */
const dynamicCssInject = (src) => {
	return new Promise((resolve) => {
		const link = document.createElement('link')
		link.rel = 'stylesheet'
		link.type = 'text/css'
		link.href = src
		document.head.appendChild(link)
		link.addEventListener('load', () => {
			resolve(link)
		})
	})
}

// Script web-vitals.js will be injected dynamically if user opts-in to sending CWV data.
const sendWebVitals = document.currentScript.getAttribute('data-cwv-src')

if (/web-vitals.js/.test(sendWebVitals)) {
	dynamicScriptInject(`${window.location.origin}/js/web-vitals.js`)
		.then(() => {
			webVitals.getCLS(sendToGoogleAnalytics)
			webVitals.getFID(sendToGoogleAnalytics)
			webVitals.getLCP(sendToGoogleAnalytics)
		})
		.catch((error) => {
			console.error(error)
		})
}

addEventListener(
	'click',
	function (e) {
		var button = e.target.closest('button')
		if (!button) {
			return
		}
		ga('send', {
			hitType: 'event',
			eventCategory: 'button',
			eventAction: button.getAttribute('aria-label') || button.textContent,
		})
	},
	true
)
var selectionTimeout
addEventListener(
	'selectionchange',
	function () {
		clearTimeout(selectionTimeout)
		var text = String(document.getSelection()).trim()
		if (text.split(/[\s\n\r]+/).length < 3) {
			return
		}
		selectionTimeout = setTimeout(function () {
			ga('send', {
				hitType: 'event',
				eventCategory: 'selection',
				eventAction: text,
			})
		}, 2000)
	},
	true
)

if (window.ResizeObserver && document.querySelector('header nav #nav')) {
	var progress = document.getElementById('reading-progress')

	var timeOfLastScroll = 0
	var requestedAniFrame = false
	function scroll() {
		if (!requestedAniFrame) {
			requestAnimationFrame(updateProgress)
			requestedAniFrame = true
		}
		timeOfLastScroll = Date.now()
	}
	addEventListener('scroll', scroll)

	var winHeight = 1000
	var bottom = 10000
	function updateProgress() {
		requestedAniFrame = false
		var percent = Math.min((document.scrollingElement.scrollTop / (bottom - winHeight)) * 100, 100)
		progress.style.transform = `translate(-${100 - percent}vw, 0)`
		if (Date.now() - timeOfLastScroll < 3000) {
			requestAnimationFrame(updateProgress)
			requestedAniFrame = true
		}
	}

	new ResizeObserver(() => {
		bottom =
			document.scrollingElement.scrollTop + document.querySelector('#comments,footer').getBoundingClientRect().top
		winHeight = window.innerHeight
		scroll()
	}).observe(document.body)
}

function expose(name, fn) {
	exposed[name] = fn
}

addEventListener('click', (e) => {
	const handler = e.target.closest('[on-click]')
	if (!handler) {
		return
	}
	e.preventDefault()
	const name = handler.getAttribute('on-click')
	const fn = exposed[name]
	if (!fn) {
		throw new Error('Unknown handler' + name)
	}
	fn(handler)
})

// There is a race condition here if an image loads faster than this JS file. But
// - that is unlikely
// - it only means potentially more costly layouts for that image.
// - And so it isn't worth the querySelectorAll it would cost to synchronously check
//   load state.
document.body.addEventListener(
	'load',
	(e) => {
		if (e.target.tagName != 'IMG') {
			return
		}
		// Ensure the browser doesn't try to draw the placeholder when the real image is present.
		e.target.style.backgroundImage = 'none'
	},
	/* capture */ 'true'
)

// import './plugins/youtube-embed.js';
/**
 * YOUTUBE
 *
 * HTML ALTERNATE
 * https://css-tricks.com/lazy-load-embedded-youtube-videos/
 */
window.addEventListener('load', (e) => {
	let YouTubeContainers = document.querySelectorAll('.embed-youtube')

	// Iterate over every YouTube container you may have
	for (let i = 0; i < YouTubeContainers.length; i++) {
		let container = YouTubeContainers[i]
		let imageSource = 'https://img.youtube.com/vi/' + container.dataset.videoId + '/sddefault.jpg'

		// Load the Thumbnail Image asynchronously
		let image = new Image()
		image.src = imageSource
		image.addEventListener('load', function () {
			container.appendChild(image)
		})

		// When the user clicks on the container, load the embedded YouTube video
		container.addEventListener('click', function () {
			let iframe = document.createElement('iframe')

			iframe.setAttribute('frameborder', '0')
			iframe.setAttribute('allowfullscreen', '')
			iframe.setAttribute(
				'allow',
				'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
			)
			// Important: add the autoplay GET parameter, otherwise the user would need to click over the YouTube video again to play it
			iframe.setAttribute(
				'src',
				'https://www.youtube.com/embed/' + this.dataset.videoId + '?rel=0&showinfo=0&autoplay=1'
			)

			// Clear Thumbnail and load the YouTube iframe
			this.innerHTML = ''
			this.appendChild(iframe)
		})
	}
})

// import "./plugins/slider.js"
/**
 * SLIDER
 */

window.addEventListener(
	'load',
	(e) => {
		if (!document.querySelector('.gallery')) return

		dynamicScriptInject(`${window.location.origin}/js/splide.min.js`)
			.then(() => {
				const main = new Splide('#main-slider', {
					type: 'slide',
					lazyLoad: 'nearby',
					rewind: true,
					pagination: false,
					arrows: true,
					cover: true,
					// width     : '90%',
					// padding: 0,
					heightRatio: 0.7,
					autoplay: true,
					interval: 5000,
					pauseOnHover: true,
				})

				const thumbnails = new Splide('#thumbnail-slider', {
					fixedWidth: 100,
					fixedHeight: 60,
					lazyLoad: 'sequential',
					// width       : '80%',
					// padding: 0,
					gap: 10,
					rewind: true,
					pagination: false,
					cover: true,
					isNavigation: true,
					breakpoints: {
						600: {
							// width: '100%',
							// height: '100%'
							fixedWidth: '18%',
							fixedHeight: 44,
						},
					},
				})

				main.sync(thumbnails)
				main.mount()
				thumbnails.mount()
			})
			.catch((error) => {
				console.error(error)
			})

		// dynamicCssInject(`${window.location.origin}/css/splide/themes/splide-default.min.css`)
		// .then(()=>{
		//   console.log('css for loaded')
		// })
		// .catch((error) => {
		//   console.error(error)
		// })
	},
	/* capture */ 'true'
)

// import "./plugins/search.js"
/**
 * SEARCH BAR
 */

let searchApi = null
window.addEventListener(
	'load',
	async (e) => {
		if (!document.querySelector('.search')) return

		await dynamicScriptInject(`${window.location.origin}/js/js-search.min.js`)
		const jsonData = await fetch(`${window.location.origin}/feed/feed.json`).then((response) => response.json()) //.then(JSON.parse)

		searchApi = new JsSearch.Search('id')
		searchApi.addIndex('title')
		searchApi.addIndex('content_html')
		// search.addIndex(['author', 'name'])

		searchApi.addDocuments(jsonData.items)

		const res = searchApi.search('ev')

		// res.map( i => alert(i.title))

		const searchBar = document.getElementById('searchBar')

		const card = (post, clasess) => {
			let bgColor = ''
			switch (post.estateType) {
				case 'sale':
					bgColor = 'bg-red-500'
					break
				case 'rent':
				case 'lease':
					bgColor = 'bg-blue-400'
					break
				default:
					bgColor = 'bg-gray-400'
			}

			return `<a href="${post.url}" class="${clasess}">
       <div class="relative mb-2 overflow-hidden text-gray-100 rounded-lg hover:opacity-100 opacity-90 aspect-w-4 aspect-h-2">
         <img class="object-cover w-full h-full thumbnail" width="100px" src="${post.image}" alt="${post.title}">
         <!-- this extra DIV tag, fix for tailwind aspect ratio settings for child elements -->
         <div>
 
   
           <div class="absolute p-1 ${bgColor} rounded-md top-2 right-2 bg-opacity-80 fadeup2 fade-delay">${post.estate}</div>
         </div>
         <div><div class="absolute p-1 mt-auto rounded-md bottom-2 left-2 right-2 bg-c1 bg-opacity-80 fadeup2 fade-delay2">
           <div class="flex flex-row justify-around">
             <div class="flex flex-col items-center">
               <div class="">${post.price} ${post.priceType}</div>
               <div class="text-xs text-gray-400">fiyat</div>
             </div>
             <div class="flex flex-col items-center">
               <div class="">${post.gross} m²</div>
               <div class="text-xs text-gray-400">brüt</div>
             </div>
             <div class="flex flex-col items-center">
               <div class="">${post.rooms}</div>
               <div class="text-xs text-gray-400">oda sayisi</div>
             </div>
           </div>
         </div></div>
       </div>
       <h2>${post.title}</h2>
     </a>`
		}

		searchBar.addEventListener('change', function (e) {
			const res = searchApi.search(searchBar.value)
			const adverts = document.getElementById('adverts2')
			adverts.innerHTML = ''

			res.map((i) => (adverts.innerHTML += card(i, '')))
		})

		// const res = searchApi.search('ev')
		// .then(async () => {
		//   const jsonData = await fetch(`${window.location.origin}/feed/feed.json`).then(response => response.json())//.then(JSON.parse)

		//   // const parsedJson = JSON.parse(jsonData)

		//   searchApi = new JsSearch.Search('id')
		//   searchApi.addIndex('title')
		//   searchApi.addIndex('content_html')
		//   // search.addIndex(['author', 'name'])

		//   searchApi.addDocuments(jsonData.items)

		//   const res = searchApi.search('ev')

		//   // search.search('The');    // [theGreatGatsby, theDaVinciCode]
		//   // search.search('scott');  // [theGreatGatsby]
		//   // search.search('dan');    // [angelsAndDemons, theDaVinciCode]
		//   // search.search('mystery') // [angelsAndDemons, theDaVinciCode]

		//   res.map( i => alert(i.title))

		// })
		// .catch((error) => {
		//   console.error(error)
		// })
	},
	/* capture */ 'true'
)

// import "./plugins/animations.js"
/**
 * ANIMATIONS
 */
window.addEventListener('load', (event) => {
	const options = {
		root: null,
		threshold: 0,
		rootMargin: '0px 0px 200px 0px',
	}

	const observer = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				entry.target.classList.add('in-view')
				observer.unobserve(entry.target)
			}
		})
	}, options)

	// const sections = Array.from(document.getElementsByClassName('section'))
	// sections.map(section => {
	//   const children = Array.from(section.getElementsByClassName('fade-delay'))
	//   children.map((child, index ) => {
	//     child.setAttribute('style', 'transition-delay: ' + index * 0.5 + 's')
	//   })
	//   observer.observe(section)
	// })

	const sections = Array.from(document.getElementsByClassName('section'))
	for (let section of sections) {
		const children = section.getElementsByClassName('fade-delay')
		for (let count = 0; count < children.length; count++) {
			children[count].setAttribute('style', 'transition-delay: ' + count * 0.5 + 's')
		}
		observer.observe(section)
	}
})

// import "./plugins/google-maps.js"
/**
 * GOOGLE MAPS
 */

window.addEventListener('load', (e) => {
	const map = document.getElementById('map')

	if (!map) return

	const options = {
		root: null,
		threshold: 0,
		rootMargin: '100px',
	}

	const observer = new IntersectionObserver((entries, self) => {
		const isIntersecting =
			typeof entries[0].isIntersecting === 'boolean' ? entries[0].isIntersecting : entries[0].intersectionRatio > 0

		if (!isIntersecting) return

		dynamicScriptInject(`https://maps.googleapis.com/maps/api/js?key=${map.dataset.key}`)
			.then(() => {
				const center = {
					lat: parseFloat(map.dataset.latitude),
					lng: parseFloat(map.dataset.longitude),
				}

				const googleMap = new google.maps.Map(map, {
					zoom: parseInt(map.dataset.zoom) || 15,
					center: center,
				})

				const marker = new google.maps.Marker({
					position: center,
					map: googleMap,
				})
			})
			.catch((error) => {
				console.error(error)
			})

		self.unobserve(map)
	}, options)
	observer.observe(map)
})
